// Camada única de acesso ao backend eaiRH (ASP.NET Core).
export const BASE = process.env.REACT_APP_API_URL || "http://localhost:5080/api";

// ---- token JWT (persistido no localStorage) ----
const TOKEN_KEY = "eairh_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);

const USER_KEY = "eairh_user";
export const getUser = () => { try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; } };
// LOGIN DE ADMIN CARIMBA O NAVEGADOR PARA SEMPRE.
//
// A trava "sessão de admin nunca conta" (donoNaoContar) depende de haver um usuário admin no localStorage —
// ou seja, de ESTAR LOGADO AGORA. Em 07/09/2026 o dono abriu /vagas num dispositivo onde não estava logado e
// entrou na própria analytics como visitante BR. Deslogar, a sessão expirar (401 limpa o USER_KEY) ou trocar
// de aparelho bastava para o opt-out sumir, e ninguém lembra de religar.
//
// Então o login de admin grava a marca MANUAL junto, que persiste depois do logout: identificar-se uma vez
// naquele navegador basta. Não cobre o navegador onde ele nunca logou — ali o caminho continua sendo abrir
// "?eairh_naocontar=1" uma vez.
export const setUser = (u) => {
  if (!u) return localStorage.removeItem(USER_KEY);
  localStorage.setItem(USER_KEY, JSON.stringify(u));
  try { if (u.ehAdmin === true) localStorage.setItem("eairh_optout", "1"); } catch { /* privado/indisponível */ }
};

// Token expirado/inválido (401) com sessão ativa: limpa a sessão e leva ao login. Compartilhado pelo request()
// e pelos uploads multipart (que não passam pelo request()), para o comportamento de sessão ser consistente.
function tratar401(res) {
  if (res.status === 401 && getToken()) {
    setToken(null);
    setUser(null);
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/entrar")) {
      window.location.href = `/entrar?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    }
  }
}

// PAUSA INVISÍVEL NO DEPLOY (19/09/2026). Durante uma publicação completa o servidor fica ~20–40s respondendo
// 503 (a página "voltamos em instantes") ou nem aceita conexão. Antes, quem clicava nesse intervalo via erro e
// perdia o que tinha digitado. Agora a chamada espera e tenta de novo sozinha por até 1 minuto.
// 503/502 = o pedido NÃO chegou ao app → repetir é seguro para qualquer método (inclusive POST).
// Falha de rede SEM resposta só é repetida em GET: num POST, o servidor pode ter gravado antes de cair.
const ESPERA_DEPLOY_MS = 60000;
// `repetivel`: a chamada pode ser refeita sem risco de duplicar (21/09/2026). Existe porque o app pool e
// reciclado pelo host varias vezes ao dia e volta em ~30s: em GET isso ja era invisivel, mas um POST pego no
// meio virava erro na cara de quem clicou em "candidatar-se". So marque assim a rota cujo servidor RECUSA a
// segunda gravacao (a de candidatura tem checagem + indice unico) — senao isto vira duplicidade silenciosa.
async function fetchComEspera(url, opts = {}, repetivel = false) {
  const ate = Date.now() + ESPERA_DEPLOY_MS;
  const ehGet = !opts.method || opts.method === "GET" || repetivel;
  let tentativas500 = 0;
  for (;;) {
    let res;
    try {
      res = await fetch(url, opts);
    } catch (e) {
      if (!ehGet || Date.now() > ate) throw e;
      await new Promise((r) => setTimeout(r, 3000));
      continue;
    }
    if ((res.status === 503 || res.status === 502) && Date.now() < ate) {
      await new Promise((r) => setTimeout(r, 3000));
      continue;
    }
    // 🩺 500 EM LEITURA TAMBÉM TENTA DE NOVO — por uma janela CURTA (25/09/2026).
    //
    // O 502/503 cobre o deploy e o app reiniciando, mas a falha mais recente não é nenhum dos dois: em
    // 23/09 e 24/09, às 23h01, o BANCO recusou conexão por alguns minutos e TODA rota de dados passou a
    // devolver 500 — com o app de pé, respondendo em 0,16s nas rotas que não tocam o banco. Para quem
    // estava navegando, virou erro na tela.
    //
    // Por que a janela é curta (e não os 60s do deploy): 503 significa "o pedido nem chegou ao app", então
    // insistir é sempre seguro. Um 500 pode ser defeito nosso de verdade — e aí segurar a pessoa um minuto
    // esperando é pior do que mostrar o erro. 3 tentativas em ~9s apanham o blip e desistem rápido.
    //
    // Só em LEITURA: repetir um POST que devolveu 500 pode gravar duas vezes.
    if (res.status === 500 && ehGet && tentativas500 < 3) {
      tentativas500++;
      await new Promise((r) => setTimeout(r, 3000));
      continue;
    }
    return res;
  }
}

// Upload multipart (FormData) — NÃO passa pelo request() (que serializa JSON). Centraliza auth, falha de rede
// e erro HTTP (incl. 401 → re-login), para os uploads terem o mesmo tratamento amigável das demais chamadas.
async function uploadMultipart(path, fd) {
  const headers = {};
  if (getToken()) headers.Authorization = `Bearer ${getToken()}`; // sem Content-Type: o browser define o boundary
  let res;
  try {
    // repetivel: o upload SOBRESCREVE o currículo do perfil, então mandar duas vezes dá no mesmo —
    // e sem isto uma reciclagem do pool no meio do envio vira "erro" para quem acabou de escolher o PDF.
    res = await fetchComEspera(`${BASE}${path}`, { method: "POST", headers, body: fd }, true);
  } catch {
    throw new Error("Sem conexão com o servidor. Verifique sua internet e tente novamente.");
  }
  if (!res.ok) {
    tratar401(res);
    let msg = `Erro ${res.status}`;
    try { const e = await res.json(); msg = e.erro || msg; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

async function request(path, { method = "GET", body, auth = false, repetivel = false } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth && getToken()) headers.Authorization = `Bearer ${getToken()}`;

  let res;
  try {
    res = await fetchComEspera(`${BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      // Leituras AUTENTICADAS (painel admin, "minhas reclamações"...) nunca podem vir do cache do navegador:
      // sem isto, o botão "↻ Atualizar" reusava a resposta cacheada do GET e os números não mudavam (só o F5,
      // que revalida, funcionava). "no-store" afeta só o cache do browser — o cache de borda do Cloudflare nas
      // APIs públicas (sem auth) segue intacto.
      cache: auth && method === "GET" ? "no-store" : undefined,
    }, repetivel);
  } catch {
    // Falha de REDE (offline, DNS, servidor inacessível): o fetch rejeita ANTES de qualquer resposta HTTP.
    // Sem isto, o componente exibiria o "Failed to fetch" cru do browser.
    throw new Error("Sem conexão com o servidor. Verifique sua internet e tente novamente.");
  }

  if (!res.ok) {
    if (auth) tratar401(res); // token expirado numa rota autenticada → limpa sessão e redireciona
    // 429 (rate limit) não traz corpo JSON; dá mensagem amigável (ex.: NAT corporativo compartilhando o IP).
    let msg = res.status === 429
      ? "Muitas tentativas em pouco tempo. Aguarde cerca de 1 minuto e tente novamente."
      : `Erro ${res.status}`;
    let extra = null;
    try { extra = await res.json(); msg = extra?.erro || msg; } catch {}
    const err = new Error(msg);
    // Repetir um POST depois de uma queda pode encontrar o trabalho JA FEITO. Quem chamou precisa distinguir
    // "deu erro" de "ja estava pronto" — sem isto a pessoa ve erro vermelho numa candidatura que existe.
    if (extra && typeof extra === "object") Object.assign(err, extra);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

// ---- Empresas ----
export const listarEmpresas = (busca) =>
  request(`/empresas${busca ? `?busca=${encodeURIComponent(busca)}` : ""}`);
export const empresasMaisCitadas = () => request(`/empresas/mais-citadas`);
export const listarEmpresasNomes = (f) => request(`/empresas/nomes${qs(f)}`); // leve (id/nome/total) p/ autocompletes; comVagas:true = só quem tem vaga aberta
export const obterEmpresa = (id) => request(`/empresas/${id}`, { auth: true }); // token opcional: backend marca "ehMeu"
export const obterFacetas = () => request(`/empresas/facetas`);
export const obterIndicadores = () => request(`/empresas/indicadores`);

// Empresa pedindo contato pela pagina /para-empresas. Publico, sem login de proposito.
export const enviarContatoParceria = (dados) =>
  request(`/parcerias`, { method: "POST", body: dados });
export const reivindicarEmpresa = (id) => request(`/empresas/${id}/reivindicar`, { method: "POST", auth: true });
// Admin: assumir uma empresa (impersonar) p/ validar o lado empresa; empresaId null = voltar a ser admin.
export const impersonarEmpresa = (empresaId) => request(`/admin/impersonar-empresa`, { method: "POST", body: { empresaId }, auth: true });
// Admin: assumir a conta de um CANDIDATO p/ conferir o currículo lido e o match do lado dele; null = voltar a ser admin.
// Admin: lista enxuta de candidatos (id/nome/temCurriculo) para o seletor de "operar como candidato".
export const listarCandidatosNomes = () => request(`/admin/candidatos-nomes`, { auth: true });
// Lista TODOS os usuarios (candidato e empresa). E a que resolve "entrar na conta da recrutadora
// que errou o e-mail" — a de candidatos nunca traria uma conta de empresa.
export const listarUsuariosNomes = (busca) => request(`/admin/usuarios-nomes${busca ? `?busca=${encodeURIComponent(busca)}` : ""}`, { auth: true });
export const impersonarUsuarioReq = (usuarioId) => request(`/admin/impersonar-usuario`, { method: "POST", body: { usuarioId }, auth: true });
// Diz se ESTA conta pode ver os blocos criticos do admin. O gate de verdade esta no servidor.
export const souDono = () => request(`/admin/sou-dono`, { auth: true });
export const impersonarCandidato = (usuarioId) => request(`/admin/impersonar-candidato`, { method: "POST", body: { usuarioId }, auth: true });
// Dicionário do espelho (admin): termos, ativação e a fila de sugestões vinda dos anúncios.
export const adminTermos = (f = {}) => request(`/admin/termos${qs(f)}`, { auth: true });
export const adminCriarTermo = (dados) => request(`/admin/termos`, { method: "POST", body: dados, auth: true });
export const adminAtivarTermo = (id, ativo, canonico) => request(`/admin/termos/${id}`, { method: "PUT", body: { ativo, canonico }, auth: true });
export const adminSugestoesTermo = (status) => request(`/admin/termos/sugestoes${qs({ status })}`, { auth: true });
export const adminDecidirSugestao = (id, acao, tipo, canonico) => request(`/admin/termos/sugestoes/${id}`, { method: "POST", body: { acao, tipo, canonico }, auth: true });
// Resumo de vagas: 1 e-mail por PESSOA com as vagas que dao match (o alerta automatico manda 1 por vaga).
export const adminDigestPrevia = () => request(`/admin/vagas/digest-previa`, { auth: true });
export const adminDigestEnviar = () => request(`/admin/vagas/digest-enviar`, { method: "POST", body: {}, auth: true });
// Recrutadores da empresa (multiusuário): a empresa lista e convida membros que operam em nome dela.
export const listarRecrutadores = () => request(`/empresas/recrutadores`, { auth: true });
// 👥 Pedidos de entrada na equipe (01/10/2026): quem criou conta com o e-mail do domínio da empresa.
export const listarPedidosEquipe = () => request(`/empresas/equipe/pedidos`, { auth: true });
export const decidirPedidoEquipe = (usuarioId, acao) => request(`/empresas/equipe/pedidos/${usuarioId}/${acao}`, { method: "POST", auth: true });
// Reenvia o convite de quem ainda não definiu senha (gera link novo — o anterior deixa de valer).
// Reenvia de uma vez para todos os convites pendentes da empresa.
export const reenviarConvitesPendentes = () =>
  request(`/empresas/recrutadores/reenviar-pendentes`, { method: "POST", auth: true });

export const reenviarConviteRecrutador = (usuarioId) =>
  request(`/empresas/recrutadores/${usuarioId}/reenviar`, { method: "POST", auth: true });

export const convidarRecrutador = (nome, email) => request(`/empresas/recrutadores`, { method: "POST", body: { nome, email }, auth: true });
// "Tenho interesse" (aceno on-platform, ≠ candidatura): empresa→candidato e candidato→vaga.
export const interesseNoCandidato = (usuarioId, mensagem) => request(`/perfil/${usuarioId}/interesse`, { method: "POST", body: { mensagem: mensagem || null }, auth: true });
export const interesseNaVaga = (vagaId, mensagem) => request(`/vagas/${vagaId}/interesse`, { method: "POST", body: { mensagem: mensagem || null }, auth: true });

// ---- Denúncias ----
export const listarDenuncias = (filtros = {}) => {
  const q = new URLSearchParams();
  if (filtros.empresa) q.set("empresa", filtros.empresa);
  if (filtros.status != null) q.set("status", filtros.status);
  if (filtros.cidade) q.set("cidade", filtros.cidade);
  if (filtros.setor) q.set("setor", filtros.setor);
  if (filtros.natureza != null) q.set("natureza", filtros.natureza);
  const qs = q.toString();
  return request(`/denuncias${qs ? `?${qs}` : ""}`, { auth: true }); // token opcional: backend marca "ehMeu"
};
export const obterDenuncia = (id) => request(`/denuncias/${id}`, { auth: true });
export const minhasDenuncias = () => request(`/denuncias/minhas`, { auth: true });
export const criarDenuncia = (dados) => request(`/denuncias`, { method: "POST", body: dados, auth: true });

// Upload de anexos (multipart). Não usa o request() porque precisa de FormData, não JSON.
export const enviarAnexos = (denunciaId, files) => {
  const fd = new FormData();
  Array.from(files).forEach((f) => fd.append("arquivos", f));
  return uploadMultipart(`/denuncias/${denunciaId}/anexos`, fd);
};
// Anexo da EMPRESA via link mágico (sem conta) — autenticado pelo token. Privado (só partes + admin veem).
export const responderTokenAnexar = (token, files) => {
  const fd = new FormData();
  Array.from(files).forEach((f) => fd.append("arquivos", f));
  return uploadMultipart(`/denuncias/responder/${token}/anexos`, fd);
};
export const responderDenuncia = (id, texto) =>
  request(`/denuncias/${id}/resposta`, { method: "POST", body: { texto }, auth: true });
// Link mágico de resposta (empresa responde SEM conta): busca o relato pelo token e envia a resposta (vai p/ moderação).
export const responderTokenObter = (token) => request(`/denuncias/responder/${token}`);
// Link de AUTORIZAÇÃO do candidato (sem conta): ele libera/retira a intermediação com a empresa.
export const autorizacaoObter = (token) => request(`/denuncias/autorizar/${token}`);
// Contato informado DEPOIS de publicar (20/09/2026): só o autor, e só com a ciência marcada por ele.
export const informarContatoRelato = (id, contato) =>
  request(`/denuncias/${id}/contato`, { method: "POST", body: { autorizar: true, ...contato }, auth: true });
// contato = { contatoEmail, contatoWhatsapp } — opcional, só para quem publicou o relato sem informar nenhum.
// São os ÚNICOS campos que este endpoint escreve: o texto do relato não muda depois de publicado.
export const autorizacaoDecidir = (token, autorizar, contato) =>
  request(`/denuncias/autorizar/${token}`, { method: "POST", body: { autorizar, ...(contato || {}) } });
export const adminLinkAutorizacao = (id) => request(`/admin/denuncias/${id}/link-autorizacao`, { method: "POST", body: {}, auth: true });
export const adminAvisarCandidatoContato = (id) => request(`/admin/denuncias/${id}/avisar-candidato-contato`, { method: "POST", body: {}, auth: true });
export const responderTokenEnviar = (token, texto, autor) =>
  request(`/denuncias/responder/${token}`, { method: "POST", body: { texto, autor } });
export const avaliarDenuncia = (id, avaliacao) =>
  request(`/denuncias/${id}/avaliacao`, { method: "POST", body: avaliacao, auth: true });
export const confirmarRetorno = (id, houveRetorno) =>
  request(`/denuncias/${id}/confirmar-retorno`, { method: "POST", body: { houveRetorno }, auth: true });
export const contestarDenuncia = (id, texto) =>
  request(`/denuncias/${id}/contestar`, { method: "POST", body: { texto }, auth: true });
export const treplicarDenuncia = (id, texto) =>
  request(`/denuncias/${id}/treplica`, { method: "POST", body: { texto }, auth: true });
export const confirmarRelato = (id) => request(`/denuncias/${id}/confirmar`, { method: "POST", auth: true });
// Resposta do candidato (autor) às atualizações da eaiRH no relato — entra privada na timeline e notifica o admin.
export const responderRelato = (id, texto) =>
  request(`/denuncias/${id}/responder`, { method: "POST", body: { texto }, auth: true });
export const contestarEmpresa = (id, texto) =>
  request(`/denuncias/${id}/contestar-empresa`, { method: "POST", body: { texto }, auth: true });
export const reportarDenuncia = (id, motivo, detalhe) =>
  request(`/denuncias/${id}/reportar`, { method: "POST", body: { motivo, detalhe }, auth: true });

// ---- Painel da empresa ----
export const obterPainel = () => request(`/painel`, { auth: true });
// Resultados das vagas da empresa: visualizações, cliques, candidaturas e série por dia (7 a 90 dias).
export const obterResultadosPainel = (dias = 30) => request(`/painel/resultados?dias=${dias}`, { auth: true });
// 💬 O que os candidatos contaram sobre os processos DESTA empresa — e a resposta dela.
// A empresa ve o conteudo, nunca o nome de quem respondeu: o feedback foi dado a NOS, com essa promessa.
export const obterFeedbacksPainel = () => request(`/painel/feedbacks`, { auth: true });
export const responderFeedbackPainel = (id, texto) =>
  request(`/painel/feedbacks/${id}/responder`, { method: "POST", body: { texto }, auth: true });
// Conector do ATS da própria empresa. O token vai de ida e nunca volta — a leitura só diz se ele existe.
export const obterConector = () => request(`/painel/conector`, { auth: true });
// Revoga usuário e token do conector (mantém o formulário). Existe porque quem cadastra precisa poder tirar.
export const removerCredenciaisConector = () => request(`/painel/conector/credenciais`, { method: "DELETE", auth: true });

export const testarConector = (body) => request(`/painel/conector/testar`, { method: "POST", body, auth: true });

// Avisos por etapa: a empresa liga, escreve o texto e escolhe a espera. Nada sai enquanto ela não ligar.
// Todas as candidaturas da empresa num lugar so — antes so existiam dentro de cada vaga.
export const candidaturasDaEmpresa = () => request(`/painel/candidaturas`, { auth: true });
export const avisosEtapa = () => request(`/painel/avisos-etapa`, { auth: true });
export const salvarAvisoEtapa = (id, body) => request(`/painel/avisos-etapa/${id}`, { method: "PUT", body, auth: true });

// ---- Perfil / currículo (estilo LinkedIn) ----
export const obterMeuPerfil = () => request(`/perfil`, { auth: true });
// Empresas que abordaram este candidato (pré-preenche o relato + cobrança direta).
export const minhasAbordagens = () => request(`/perfil/me/abordagens`, { auth: true });
// Convites/oportunidades recebidos (abordagens da empresa a vagas ou ao perfil) — mensagem já com variáveis resolvidas.
export const meusConvites = () => request(`/perfil/me/convites`, { auth: true });
export const obterPerfilPublico = (usuarioId) => request(`/perfil/${usuarioId}`, { auth: true });
// Perfil por LINK PERMANENTE (sem login): é o endereço impresso no currículo que a empresa encaminha adiante.
export const perfilPublicoPorToken = (token) => request(`/perfil/publico/${token}`);
export const salvarPerfil = (dados) => request(`/perfil`, { method: "PUT", body: dados, auth: true });

// 🎯 As skills que mais abririam vagas PARA ESTA PESSOA (recorte de área e cidade do currículo dela) e
// onde aprender cada uma. O número é "suas vagas", não "vagas do Brasil" — é o que faz diferença.
export const skillsFaltantes = () => request(`/perfil/skills-faltantes`, { auth: true });
// Competência que está na lista de skills e não aparece em nenhuma descrição de experiência — o ponto
// que mais pesa na ordenação dos ATS. Ver a rota /skills-soltas.
export const skillsSoltas = () => request(`/perfil/skills-soltas`, { auth: true });
// Cursos de uma lista de skills ESPECÍFICAS — usado pelo popup aberto na página da vaga, onde a pergunta
// é "o que falta para ESTA vaga" e não "o que abriria mais vagas no geral".
// 📎 Sobe o comprovante de um certificado já criado. Multipart, como o upload do currículo.
export async function anexarCertificado(certificadoId, arquivo) {
  const fd = new FormData();
  fd.append("arquivo", arquivo);
  const headers = {};
  if (getToken()) headers.Authorization = `Bearer ${getToken()}`;
  const res = await fetch(`${BASE}/perfil/certificado/${certificadoId}/anexo`, { method: "POST", headers, body: fd });
  if (!res.ok) {
    tratar401(res);
    let msg = `Erro ${res.status}`;
    try { const e = await res.json(); msg = e.erro || msg; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

export const cursosDeSkills = (skills) =>
  request(`/perfil/cursos-de-skills?skills=${encodeURIComponent((skills || []).join(","))}`, { auth: true });
// "Já fiz este curso": a skill vai para o match, o curso vai para os certificados. São dois destinos.
export const cursoConcluido = (dados) => request(`/perfil/curso-concluido`, { method: "POST", body: dados, auth: true });
// 🏛️ Instituições já conhecidas (catálogo + o que outros candidatos digitaram), para o campo sugerir em
// vez de deixar livre — senão a mesma escola vira "Udemy", "udemy" e "UDEMY" no banco.
export const instituicoesConhecidas = () => request(`/perfil/instituicoes`);
// Busca de candidatos pelo recrutador (só empresa verificada; backend filtra por consentimento). Filtros co-criados c/ a Juliana (Nextal).
export const buscarCandidatos = (f) => request(`/perfil/buscar${qs(f)}`, { auth: true });
export const enviarCurriculo = (file) => {
  const fd = new FormData();
  fd.append("arquivo", file);
  return uploadMultipart(`/perfil/curriculo`, fd);
};
// UFs e municípios do IBGE (seletor "UF → cidade" do currículo). Público, sem login.
export const listarUfs = () => request(`/localidades`);
export const listarMunicipios = (uf) => request(`/localidades/${encodeURIComponent(uf)}`);
// Conta ao servidor uma falha de envio que ele não viu (rede caiu, arquivo barrado no celular). Nunca lança.
export const reportarFalhaCurriculo = (dados) =>
  request(`/perfil/curriculo/falha`, { method: "POST", body: dados, auth: true }).catch(() => {});

// Logo da empresa: upload (empresa logada + verificada) + URL pública para exibir o logo.
export const enviarLogoEmpresa = (file) => {
  const fd = new FormData();
  fd.append("arquivo", file);
  return uploadMultipart(`/empresas/logo`, fd);
};
export const empresaLogoUrl = (id) => `${BASE}/empresas/${id}/logo`;

// Baixa o currículo ANEXADO por um candidato (empresa verificada, dona da vaga — o backend confere).
// Mesmo padrão do download autenticado: token no header, então não dá pra usar <a href> direto.
export async function baixarCurriculoCandidato(usuarioId) {
  const headers = {};
  if (getToken()) headers.Authorization = `Bearer ${getToken()}`;
  let res;
  try { res = await fetch(`${BASE}/perfil/curriculo/${usuarioId}`, { headers }); }
  catch { throw new Error("Sem conexão com o servidor. Verifique sua internet e tente novamente."); }
  if (!res.ok) {
    tratar401(res);
    let msg = res.status === 404 ? "Este candidato não anexou currículo." : `Erro ${res.status}`;
    try { const e = await res.json(); msg = e.erro || msg; } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  const nome = m ? decodeURIComponent(m[1].replace(/"/g, "")) : "curriculo";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nome;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// 18/10/2026 (Nelson: "não tem como o PDF abrir numa aba do modal em vez de baixar?"): busca o arquivo autenticado e
// devolve um endereço local (blob) para mostrar num <iframe> ou abrir em nova aba. Quem usa libera com URL.revokeObjectURL.
export async function arquivoParaVer(caminho) {
  const headers = {};
  if (getToken()) headers.Authorization = `Bearer ${getToken()}`;
  let res;
  try { res = await fetch(`${BASE}${caminho}`, { headers }); }
  catch { throw new Error("Sem conexão com o servidor. Verifique sua internet e tente novamente."); }
  if (!res.ok) {
    tratar401(res);
    let msg = res.status === 404 ? "Arquivo não encontrado." : `Erro ${res.status}`;
    try { const e = await res.json(); msg = e.erro || msg; } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  const nome = m ? decodeURIComponent(m[1].replace(/"/g, "")) : "arquivo";
  const pdf = (blob.type || "").includes("pdf") || /\.pdf$/i.test(nome);
  return { url: URL.createObjectURL(pdf ? new Blob([blob], { type: "application/pdf" }) : blob), nome, pdf };
}

// Currículo no PADRÃO eaiRH de OUTRA pessoa — quem usa é a empresa, na lista de candidaturas.
// Mesmo modelo do PDF que o candidato baixa de si; muda só quem pede (e o backend confere a permissão).
export async function baixarCurriculoPadrao(usuarioId) {
  const headers = {};
  if (getToken()) headers.Authorization = `Bearer ${getToken()}`;
  let res;
  try { res = await fetch(`${BASE}/perfil/curriculo-gerado/${usuarioId}`, { headers }); }
  catch { throw new Error("Sem conexão com o servidor. Verifique sua internet e tente novamente."); }
  if (!res.ok) {
    tratar401(res);
    let msg = `Erro ${res.status}`;
    try { const e = await res.json(); msg = e.erro || msg; } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  const nome = m ? decodeURIComponent(m[1].replace(/"/g, "")) : "Curriculo.pdf";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nome;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Baixa o currículo GERADO em PDF (montado a partir dos dados do perfil, no modelo do site).
// Download autenticado (blob) — o token vai no header, então não dá pra usar <a href> direto.
// `modelo`: padrão = formatado em duas colunas (29/09/2026); "ats" = coluna única, para anexar em sistema
// de triagem. Ver a rota /curriculo-gerado.
export async function baixarCurriculoGerado(modelo) {
  const headers = {};
  if (getToken()) headers.Authorization = `Bearer ${getToken()}`;
  const qs = modelo ? `?modelo=${encodeURIComponent(modelo)}` : "";
  let res;
  try { res = await fetch(`${BASE}/perfil/curriculo-gerado${qs}`, { headers }); }
  catch { throw new Error("Sem conexão com o servidor. Verifique sua internet e tente novamente."); }
  if (!res.ok) {
    tratar401(res);
    let msg = `Erro ${res.status}`;
    try { const e = await res.json(); msg = e.erro || msg; } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  const nome = m ? decodeURIComponent(m[1].replace(/"/g, "")) : "Curriculo.pdf";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nome;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Setores/áreas de atuação — UNIVERSAL (do operacional ao executivo, todos os ramos). Ordem alfabética, "Outro" por último.
export const AREAS_ATUACAO = [
  "Administração / Escritório", "Agronegócio", "Alimentação / Restaurantes", "Arquitetura / Design",
  "Arte / Cultura", "Atendimento / Call Center", "Automotivo", "Beleza e Bem-estar",
  "Comércio / Varejo", "Construção Civil", "Consultoria", "Contabilidade / Finanças",
  "Educação / Ensino", "Energia / Utilities", "Engenharia", "Entretenimento / Audiovisual",
  "Esportes / Fitness", "Eventos", "Farmacêutica", "Financeiro / Bancos / Seguros",
  "Governo / Setor Público", "Hotelaria / Turismo", "Imobiliário", "Indústria / Manufatura",
  "Jurídico / Direito", "Logística / Transporte", "Manutenção / Reparos",
  "Marketing / Publicidade / Comunicação", "Meio Ambiente / Sustentabilidade", "Mídia / Jornalismo",
  "Mineração", "Moda / Têxtil", "ONGs / Terceiro Setor", "Petróleo e Gás", "Pesquisa / Ciência",
  "Recursos Humanos", "Saneamento / Coleta de Resíduos", "Saúde / Hospitalar", "Segurança / Vigilância",
  "Serviços Domésticos", "Serviços Gerais / Limpeza", "Tecnologia da Informação", "Telecomunicações",
  "Transporte / Mobilidade", "Vendas / Comercial", "Veterinária / Pet", "Outro",
];

// Senioridade/nível — cobre do operacional (sem hierarquia corporativa) ao C-level.
export const SENIORIDADES = [
  "Aprendiz", "Estágio", "Trainee", "Auxiliar / Assistente", "Operacional", "Júnior", "Pleno",
  "Sênior", "Especialista", "Encarregado / Líder", "Supervisão", "Coordenação", "Gerência", "Diretoria",
];

// Skills sugeridas (autocomplete) — UNIVERSAL, todas as profissões. TI é só uma categoria entre muitas.
export const SKILLS_SUGERIDAS = [
  // Competências gerais (servem a qualquer profissão)
  "Comunicação", "Trabalho em equipe", "Pontualidade", "Proatividade", "Organização", "Responsabilidade",
  "Ética profissional", "Trabalho sob pressão", "Flexibilidade", "Atenção aos detalhes", "Resolução de problemas",
  "Liderança", "Negociação", "Pensamento crítico", "Adaptabilidade", "Inteligência emocional", "Gestão de tempo",
  "Empatia", "Criatividade", "Aprendizado rápido",
  // Atendimento, vendas e comércio
  "Atendimento ao cliente", "Vendas", "Operador de caixa", "Telemarketing", "Pós-venda", "Visual merchandising",
  "Recepção", "Reposição de mercadorias",
  // Administrativo / financeiro
  "Pacote Office", "Excel", "Digitação", "Rotinas administrativas", "Arquivo", "Faturamento",
  "Contas a pagar", "Contas a receber", "Departamento pessoal", "Emissão de notas fiscais", "Agendamento",
  "Gestão financeira", "Contabilidade", "Controladoria",
  // Idiomas
  "Inglês", "Espanhol", "Libras",
  // Transporte e condução
  "CNH A", "CNH B", "CNH C", "CNH D", "CNH E", "Direção defensiva", "Operação de empilhadeira",
  "Motorista de caminhão", "Motoboy",
  // Logística e estoque
  "Controle de estoque", "Almoxarifado", "Conferência de mercadorias", "Expedição", "Picking e packing",
  // Construção e manutenção
  "Alvenaria / Pedreiro", "Pintura", "Instalações elétricas", "Instalações hidráulicas", "Carpintaria",
  "Marcenaria", "Soldagem", "Serralheria", "Gesso / Drywall", "Leitura de projetos", "Manutenção predial",
  "Manutenção mecânica", "Refrigeração e ar-condicionado", "Jardinagem",
  // Indústria e produção
  "Operação de máquinas", "Linha de produção", "Controle de qualidade", "Manutenção industrial", "Usinagem",
  // Serviços gerais, limpeza e saneamento
  "Limpeza e conservação", "Coleta de resíduos", "Higienização", "Controle de pragas", "Zeladoria",
  // Segurança
  "Vigilância", "Segurança patrimonial", "Porteiro / Portaria", "Brigada de incêndio", "Primeiros socorros",
  // Alimentação
  "Cozinha", "Confeitaria", "Panificação", "Churrasqueiro", "Garçom / Atendimento de mesa", "Barista",
  "Preparo de alimentos", "Higiene alimentar",
  // Beleza e bem-estar
  "Corte de cabelo", "Coloração", "Manicure e pedicure", "Maquiagem", "Design de sobrancelhas",
  "Estética facial", "Massoterapia", "Barbearia",
  // Saúde e cuidados
  "Enfermagem", "Técnico de enfermagem", "Cuidador de idosos", "Babá", "Auxiliar de saúde bucal", "Fisioterapia",
  // Educação
  "Docência", "Didática", "Educação infantil", "Tutoria", "Preparação de aulas",
  // Doméstico
  "Serviços domésticos", "Cozinhar", "Passar roupa", "Cuidados com crianças",
  // Agro e pet
  "Manejo agrícola", "Operação de trator", "Pecuária", "Tosa e banho", "Adestramento",
  // Gestão e negócios
  "Gestão de Projetos", "Gestão de pessoas", "Gestão de equipes", "Planejamento estratégico", "Análise de dados",
  // Marketing e criação
  "Marketing digital", "Redes sociais", "Copywriting", "Design gráfico", "Edição de vídeo", "Fotografia", "SEO",
  // Tecnologia da Informação (uma categoria entre muitas)
  "Informática básica", "Excel avançado", "Suporte técnico", "Redes de computadores", "Python", "JavaScript",
  "SQL", "Power BI", "Pacote Adobe", "UiPath / RPA", "Inteligência Artificial", "Cloud (AWS / Azure)",
];

// Sugestões de cidade (datalist) — principais cidades BR + modelos de trabalho.
export const CIDADES = [
  "Remoto", "Híbrido", "São Paulo", "Rio de Janeiro", "Belo Horizonte", "Brasília", "Curitiba",
  "Porto Alegre", "Salvador", "Recife", "Fortaleza", "Campinas", "Florianópolis", "Goiânia",
  "Manaus", "Belém", "Vitória", "Natal", "João Pessoa", "Maceió", "Cuiabá", "Campo Grande",
  "São José dos Campos", "Ribeirão Preto", "Sorocaba", "Santos", "Joinville", "Londrina",
  "Uberlândia", "Caxias do Sul", "Osasco", "Guarulhos",
];

// Sugestões de benefícios (datalist).
export const BENEFICIOS = [
  "Vale-refeição (VR)", "Vale-alimentação (VA)", "Vale-transporte (VT)", "Plano de saúde",
  "Plano odontológico", "Seguro de vida", "Gympass / Wellhub", "Auxílio home office",
  "Horário flexível", "Day off no aniversário", "PLR (participação nos lucros)", "Bônus",
  "Previdência privada", "Auxílio creche", "Vale-cultura", "Auxílio educação", "Plano de carreira",
  "Stock options", "Licença parental estendida",
];
export const DISPONIBILIDADES = [
  { v: 0, t: "🟢 Disponível" },
  { v: 1, t: "🔵 Aberto a propostas" },
  { v: 2, t: "⚪ Não buscando" },
];

// ---- Vagas + Match ----
const qs = (o) => {
  const p = new URLSearchParams();
  Object.entries(o || {}).forEach(([k, v]) => { if (v !== "" && v != null) p.set(k, v); });
  const s = p.toString();
  return s ? `?${s}` : "";
};
export const listarVagas = (f) => request(`/vagas${qs(f)}`);

// 📊 UM PASSO DO FUNIL DA VAGA. Anônimo, sem corpo e sem esperar resposta: serve para comparar handoff x
// candidatura pelo eaiRH, e nunca pode atrapalhar a navegação de quem está tentando se candidatar.
// 🚪 PASSO DO /entrar (06/10/2026). Mesmo funil anônimo, vaga 0. Usa sendBeacon porque o passo que mais importa —
// "saiu sem entrar" — acontece quando a aba está fechando, e um fetch comum morre junto com ela.
export const passoEntrar = (etapa) => {
  try {
    const url = `${BASE}/vagas/0/funil?etapa=${encodeURIComponent(etapa)}`;
    if (navigator.sendBeacon && navigator.sendBeacon(url, "")) return;
    fetch(url, { method: "POST", keepalive: true }).catch(() => {});
  } catch { /* medição nunca atrapalha a entrada */ }
};
export const passoFunilVaga = (id, etapa) => {
  try { request(`/vagas/${id}/funil?etapa=${etapa}`, { method: "POST" }).catch(() => {}); } catch { /* nada */ }
};
// auth quando há sessão: a leitura da vaga é pública, mas o Worker cacheia 3 min na borda toda GET pública SEM
// credencial. Para a EMPRESA dona isso escondia o próprio trabalho — ela mudava o status, a tela recarregava e
// recebia de volta a cópia cacheada de minutos antes, com o valor velho. Com o token no cabeçalho o Worker não
// cacheia e o navegador vai de no-store; para quem não tem sessão nada muda e o cache de borda segue valendo.
export const obterVaga = (id) => request(`/vagas/${id}`, { auth: true });
// Mesma vaga pelo endereço legível (/gateware/analista-de-negocios-senior).
// Caminho público de uma vaga já carregada. Usa o endereço legível quando ele existe e cai no Id enquanto não
// existe — os dois funcionam, mas é o legível que a pessoa copia da barra e manda para alguém.
export const caminhoVaga = (v) => (v && v.url) || `/vagas/${v?.id ?? v}`;

export const obterVagaPorSlug = (empresaSlug, vagaSlug) => request(`/vagas/slug/${empresaSlug}/${vagaSlug}`, { auth: true });
export const vagasRecomendadas = (f) => request(`/vagas/recomendadas${qs(f)}`, { auth: true });
export const minhasVagas = () => request(`/vagas/minhas`, { auth: true });
export const criarVaga = (dados) => request(`/vagas`, { method: "POST", body: dados, auth: true });
// ✏️ 20/10/2026: edição da vaga pela empresa dona (campos crus para o formulário + PUT).
export const obterVagaEdicao = (id) => request(`/vagas/${id}/edicao`, { auth: true });
export const atualizarVaga = (id, dados) => request(`/vagas/${id}`, { method: "PUT", body: dados, auth: true });
export const analisarVagaTexto = (texto) => request(`/vagas/analisar-texto`, { method: "POST", body: { texto }, auth: true });
// Skills que os candidatos já cadastraram (mais usadas primeiro) — sugestão no formulário de vaga.
export const skillsSugeridas = () => request(`/vagas/skills-sugeridas`);
export const candidatosDaVaga = (id, f) => request(`/vagas/${id}/candidatos${qs(f)}`, { auth: true });
// Caminho inverso: em QUAIS vagas da empresa este candidato encaixa (com match e "já abordado").
export const vagasCompativeisCom = (usuarioId) => request(`/vagas/compativeis-com/${usuarioId}`, { auth: true });
export const importarVagas = (termo, cidade) => request(`/vagas/importar${qs({ termo, cidade })}`, { method: "POST", auth: true });
// canais: { porEmail, porWhatsapp } — omitido = usa a preferência do candidato (comportamento antigo).
// Registra um retorno que a empresa já deu pelo canal DELA. Não notifica ninguém — só encerra a dívida aqui.
// `canal` (20/09/2026): por onde a empresa diz ter respondido. Vai na mensagem que o CANDIDATO recebe
// avisando dessa declaracao — a rota aceita nulo, mas a tela sempre manda.
export const registrarRetornoExterno = (vagaId, usuarioId, canal) =>
  request(`/vagas/${vagaId}/retorno-externo/${usuarioId}`, { method: "POST", body: { canal }, auth: true });

export const darRetornoVaga = (vagaId, usuarioId, status, mensagem, canais) =>
  request(`/vagas/${vagaId}/retorno/${usuarioId}`, { method: "POST", body: { status, mensagem, ...(canais || {}) }, auth: true });
// 🔒 20/10/2026: declaroTer = skills obrigatórias que o candidato DECLARA ter (entram no currículo e liberam a candidatura).
export const candidatarVaga = (id, mensagem, lote = false, declaroTer = null) => request(`/vagas/${id}/candidatar`, { method: "POST", body: { mensagem, lote, declaroTer }, auth: true, repetivel: true });
// 🔎 CANDIDATURA EM LOTE (29/09/2026). `de` = marcador do post que trouxe a pessoa (?de=rpa, ?de=varejo…).
export const vagasSemelhantes = (id, de) => request(`/vagas/${id}/semelhantes${de ? `?de=${encodeURIComponent(de)}` : ""}`, { auth: true });
// Fim do lote: UM e-mail só com tudo (o servidor confere o que é verdade antes de escrever).
export const resumoDoLote = (dados) => request(`/vagas/lote/resumo`, { method: "POST", body: dados, auth: true });
// Por onde a candidatura saiu: "ats" | "email" | "link" | "tentando". O modal consulta isto enquanto a
// cadeia acontece no servidor, para narrar ao candidato em vez de dizer "enviada!" e deixar no escuro.
export const estadoEntrega = (id) => request(`/vagas/${id}/estado-entrega`, { auth: true });
// A pergunta "conseguiu se candidatar?" nao pode depender da aba continuar aberta: quem recarrega ou
// volta pelo celular perdia o estado e nunca mais era perguntado, mesmo com a ida gravada no banco.
export const minhaIntencao = (id) => request(`/vagas/${id}/minha-intencao`, { auth: true });

// CANDIDATURA DECLARADA — vaga que só recebe no site da empresa.
// A MESMA chamada serve aos três momentos do fluxo: declarar que se candidatou, avisar que não conseguiu,
// e completar depois os dados de quem entrou em contato. Campo que não vier não apaga o que já existe.
export const declararCandidatura = (id, dados) =>
  request(`/vagas/${id}/declarar`, { method: "POST", body: dados, auth: true });
// "NÃO CONSEGUI" — POR QUÊ. Informação nossa, não relato (regra do Nelson, 11/09/2026): nunca vai para a
// empresa nem entra na reputação. A mesma lista serve à tela da vaga e à página do link do e-mail.
export const MOTIVOS_NAO_CONSEGUIU = [
  { v: "curriculo_arquivo", t: "Pediu currículo em arquivo (PDF ou Word) e eu não tinha" },
  { v: "cadastro_login", t: "Pediu para criar conta ou fazer login" },
  { v: "formulario_longo", t: "O formulário era longo demais ou pedia dados que eu não tinha" },
  { v: "erro_site", t: "O site deu erro ou não carregou" },
  { v: "vaga_encerrada", t: "A vaga estava encerrada lá" },
  { v: "outro", t: "Outro motivo" },
];
export const obterMotivoNaoConseguiu = (token) => request(`/intencao/motivo/${token}`);
export const enviarMotivoNaoConseguiu = (token, body) => request(`/intencao/motivo/${token}`, { method: "POST", body });
export const alterarStatusVaga = (id, status) => request(`/vagas/${id}/status`, { method: "PUT", body: { status }, auth: true });
// Encerramento cordial: marca a vaga como preenchida + avisa os candidatos (contratado + demais) com msgs parametrizáveis pelos canais escolhidos.
export const encerrarVaga = (id, dados) => request(`/vagas/${id}/encerrar`, { method: "POST", body: dados, auth: true });
// Abordagem ativa: empresa convida candidatos selecionados à vaga (mensagem + canais + link da vaga + assinatura).
export const abordarCandidatos = (id, dados) => request(`/vagas/${id}/abordar`, { method: "POST", body: dados, auth: true });
export const candidaturasDaVaga = (id) => request(`/vagas/${id}/candidaturas`, { auth: true });
export const minhasCandidaturas = () => request(`/vagas/minhas-candidaturas`, { auth: true });
// Link direto de feedback da candidatura: SEM auth de proposito — exigir login mata a resposta.
export const obterFeedbackCandidatura = (token) => request(`/vagas/feedback/${token}`);
export const enviarFeedbackCandidatura = (token, body) => request(`/vagas/feedback/${token}`, { method: "POST", body });
// Etapa contada pelo próprio candidato — um clique, sem login. É o único sensor de andamento onde não há ATS
// integrado (a maioria dos casos: Gupy, Greenhouse e afins não expõem etapa sem acordo).
export const marcarEtapaCandidatura = (token, etapa) =>
  request(`/vagas/feedback/${token}/etapa?etapa=${etapa}`, { method: "POST" });
export const STATUS_VAGA = [
  { v: 0, t: "Aberta" }, { v: 1, t: "Em andamento" }, { v: 2, t: "Pausada" }, { v: 3, t: "Finalizada" },
];
export const RETORNOS_VAGA = ["Currículo recebido", "Convidado para entrevista", "Em análise", "Avançou de etapa", "Não selecionado desta vez"];

// ---- Modelos de encerramento da EMPRESA (3 tipos) + motivos de encerramento sem contratação ----
// Tags do encerramento (chaves, iguais às que o backend substitui). {motivo} só existe no tipo "Sem contratação".
export const TAGS_ENCERRAMENTO = ["{nome}", "{vaga}", "{empresa}"];
export const TIPOS_MODELO_ENCERRAMENTO = [
  { v: 0, t: "Aprovado", ajuda: "Para quem foi selecionado." },
  { v: 1, t: "Não aprovados", ajuda: "Para quem participou e não seguiu." },
  { v: 2, t: "Sem contratação", ajuda: "Quando a vaga encerrou sem contratar ninguém. Aqui você pode usar a tag {motivo}." },
];
// Motivos padrão do sistema (a empresa pode criar os dela). Texto = como entra no lugar de {motivo}.
export const MOTIVOS_ENCERRAMENTO_PADRAO = [
  { rotulo: "Vaga cancelada", texto: "a vaga foi cancelada" },
  { rotulo: "Vaga congelada", texto: "a vaga foi suspensa por decisão interna" },
  { rotulo: "Preenchida internamente", texto: "a posição foi preenchida por movimentação interna" },
  { rotulo: "Não encontramos o perfil", texto: "não encontramos o perfil que a posição exigia neste momento" },
];
export const modelosEncerramento = () => request(`/empresas/me/modelos-encerramento`, { auth: true });
export const criarModeloEncerramento = (dados) => request(`/empresas/me/modelos-encerramento`, { method: "POST", body: dados, auth: true });
export const salvarModeloEncerramento = (id, dados) => request(`/empresas/me/modelos-encerramento/${id}`, { method: "PUT", body: dados, auth: true });
export const excluirModeloEncerramento = (id) => request(`/empresas/me/modelos-encerramento/${id}`, { method: "DELETE", auth: true });
export const motivosEncerramento = () => request(`/empresas/me/motivos-encerramento`, { auth: true });
export const criarMotivoEncerramento = (dados) => request(`/empresas/me/motivos-encerramento`, { method: "POST", body: dados, auth: true });
export const excluirMotivoEncerramento = (id) => request(`/empresas/me/motivos-encerramento/${id}`, { method: "DELETE", auth: true });

// ---- Auth ----
export const registrar = (dados) => request(`/auth/registro`, { method: "POST", body: dados, repetivel: true });
export const confirmarCodigo = (email, codigo) => request(`/auth/confirmar-codigo`, { method: "POST", body: { email, codigo } });
export const reenviarCodigo = (email, destino) => request(`/auth/reenviar-codigo`, { method: "POST", body: { email, destino } });
export const login = (email, senha) => request(`/auth/login`, { method: "POST", body: { email, senha } });
// 🔑 Entrar com o Google (29/09/2026): o servidor confere o ID token no Google antes de entrar/criar a conta.
export const googleConfig = () => request(`/auth/google/config`);
// 🔗 LinkedIn (29/09/2026): a ida é navegação de página inteira (o LinkedIn não devolve token ao site).
export const linkedinConfig = () => request(`/auth/linkedin/config`);
export const urlEntrarLinkedin = (destino) => `${BASE}/auth/linkedin/inicio${destino ? `?destino=${encodeURIComponent(destino)}` : ""}`;
export const loginGoogle = (credential, destino) => request(`/auth/google`, { method: "POST", body: { credential, destino } });
export const salvarPreferencias = (notificarEmail, notificarWhatsapp, receberVagas) =>
  request(`/auth/preferencias`, { method: "PUT", body: { notificarEmail, notificarWhatsapp, receberVagas }, auth: true });
export const obterConta = () => request(`/auth/conta`, { auth: true });
export const salvarConta = (nome, whatsapp, assinaturaOutreach) => request(`/auth/conta`, { method: "PUT", body: { nome, whatsapp, assinaturaOutreach }, auth: true });

export const trocarSenha = (senhaAtual, senhaNova) =>
  request(`/auth/senha`, { method: "PUT", body: { senhaAtual, senhaNova }, auth: true });

export const esqueciSenha = (email) =>
  request(`/auth/esqueci-senha`, { method: "POST", body: { email } });
export const redefinirSenha = (token, senhaNova) =>
  request(`/auth/redefinir-senha`, { method: "POST", body: { token, senhaNova } });
export const confirmarEmail = (token) =>
  request(`/auth/confirmar-email`, { method: "POST", body: { token } });

// ---- Analytics de visita (funil + engajamento). Fire-and-forget, sem PII, respeita opt-out/bot no backend. ----
// Sessão ANÔNIMA e efêmera: id aleatório em sessionStorage (some ao fechar a aba; não é cookie, não identifica a pessoa).
function getSessionId() {
  try {
    let s = sessionStorage.getItem("eairh_sid");
    if (!s) {
      s = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID()
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("eairh_sid", s);
    }
    return s;
  } catch { return "anon"; }
}

function envia(path, body) {
  try {
    const json = JSON.stringify(body);
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(`${BASE}${path}`, new Blob([json], { type: "application/json" }));
    } else {
      fetch(`${BASE}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: json, keepalive: true }).catch(() => {});
    }
  } catch { /* analytics jamais interrompe a navegação */ }
}

// Opt-out do DONO (robusto, client-side): além do cookie no backend, marcamos no localStorage deste navegador.
// Persiste de verdade e não depende do cookie chegar em cada beacon — os beacons simplesmente NÃO disparam.
//
// SESSÃO DE ADMIN NUNCA CONTA — e essa é a trava que realmente segura. A marca manual (localStorage/cookie)
// vale por ORIGEM: quando o site saiu de eairh.runasp.net para eairh.com.br, o opt-out ficou para trás e o
// dono voltou a entrar na conta silenciosamente. O mesmo acontece em outro navegador, em aba anônima e no
// celular. Já o "estou logado como admin" viaja com a sessão, então cobre todos esses casos sem ninguém
// precisar lembrar de religar nada.
//
// Vale também quando o admin está personificando empresa ou candidato (o `ehAdmin` sobrevive ao impersonate):
// ali ele está testando o produto, e teste do dono não é audiência. Quem opera o painel é a mesma pessoa que
// lê o número — se ele se conta, o número deixa de servir para a única coisa que importa, que é saber se
// alguém de fora chegou.
export function donoNaoContar() {
  try {
    if (localStorage.getItem("eairh_optout") === "1") return true;
    return getUser()?.ehAdmin === true;
  } catch { return false; }
}
// Liga/desliga o opt-out direto (usado pelo botão no menu do admin). Dispara um evento p/ a UI reagir na hora.
export function setDonoOptOut(ativar) {
  try {
    if (ativar) localStorage.setItem("eairh_optout", "1");
    else localStorage.removeItem("eairh_optout");
    window.dispatchEvent(new Event("eairh-optout"));
  } catch { /* privado/indisponível */ }
}

// Lê ?eairh_naocontar=1 (liga) / ?eairh_contar=1 (desliga) e persiste a escolha. Retorna o estado atual.
export function aplicarOptOut(search) {
  try {
    const q = new URLSearchParams(search || "");
    if (q.has("eairh_naocontar")) { localStorage.setItem("eairh_optout", "1"); return true; }
    if (q.has("eairh_contar")) { localStorage.removeItem("eairh_optout"); return false; }
  } catch { /* privado/indisponível */ }
  return donoNaoContar();
}

// Id do VISITANTE (localStorage, persiste entre visitas) — diz se a pessoa é nova ou está voltando.
// É um número sorteado, sem nome, sem e-mail, sem IP; serve só ao painel admin. Nunca aparece em público.
function getVisitorInfo() {
  try {
    let v = localStorage.getItem("eairh_vid");
    if (v) return { v, n: false };                 // já existia → visitante recorrente
    v = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID()
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("eairh_vid", v);
    return { v, n: true };                          // acabou de nascer → visitante novo
  } catch { return { v: null, n: false }; }         // navegação privada/bloqueado: só não classifica
}

// Registra a ROTA vista (funil) + marca atividade da sessão. Chamado a cada troca de rota no SPA.
// Manda também o document.referrer: é ele que diz de ONDE a pessoa veio (LinkedIn, Google, direto). O backend
// conta a visita na primeira chamada da sessão — o GET do HTML não serve mais para isso porque o Cloudflare
// o entrega do cache. Nas trocas de rota seguintes o referrer é o próprio site e o backend já ignora.
export function registrarVisita(path) {
  if (donoNaoContar()) return; // dono: não conta
  const { v, n } = getVisitorInfo();
  let r = "";
  try { r = document.referrer || ""; } catch { /* iframe/privado */ }
  envia("/track", { p: path, s: getSessionId(), v, n, r, d: marcadorDeOrigem() });
}

// 🏷️ DE ONDE A PESSOA VEIO, QUANDO O REFERRER NÃO BASTA (24/09/2026).
//
// O referrer diz "LinkedIn" e para por aí: feed, página, grupo e mensagem chegam todos como a mesma
// palavra. E o LinkedIn é 4.222 dos acessos do portal — 15× mais que o Google —, então "LinkedIn"
// sozinho é a resposta menos útil possível quando a pergunta é ONDE postar.
//
// Com `?de=uipath-rpa` no link postado, o marcador viaja até o painel e a semana seguinte diz qual
// grupo trouxe gente e qual só deu trabalho.
//
// Guardado na SESSÃO: a pessoa chega pelo link marcado e navega por mais cinco páginas; sem isso só a
// primeira contaria, e o marcador perderia justamente quem se interessou mais.
function marcadorDeOrigem() {
  try {
    const q = new URLSearchParams(window.location.search);
    const novo = (q.get("de") || "").trim().slice(0, 40);
    if (novo) sessionStorage.setItem("eairh_de", novo);
    return sessionStorage.getItem("eairh_de") || "";
  } catch { return ""; }   // privado/bloqueado: segue sem marcador, nunca quebra a visita
}

// Heartbeat de sessão (a cada ~20s enquanto a aba está visível): alimenta "visitas ativas" + "tempo de visita".
export function heartbeat() {
  if (donoNaoContar()) return; // dono: não conta
  envia("/hb", { s: getSessionId() });
}

// ---- IA (Claude) ----
// Chat do assistente. Só-logado (auth). Retorna { resposta } ou { resposta:null, indisponivel:true }.
export const iaChat = (mensagem, historico) => request(`/ia/chat`, { method: "POST", body: { mensagem, historico }, auth: true });

// Engajamento e tempo de retorno
export const marcarUtil = (id) => request(`/denuncias/${id}/util`, { method: "POST" });
export const definirDiasRetorno = (id, dias) =>
  request(`/denuncias/${id}/dias-retorno`, { method: "PUT", body: { dias }, auth: true });
export const salvarEmpresaPerfil = (dados) => request(`/empresas/me`, { method: "PUT", body: dados, auth: true });
// Config interna da empresa (e-mail que recebe as candidaturas). Fora do DTO público de propósito.
export const obterConfigEmpresa = () => request(`/empresas/me/config`, { auth: true });

// ---- Notificações ----
export const listarNotificacoes = () => request(`/notificacoes`, { auth: true });
export const marcarNotificacaoLida = (id) => request(`/notificacoes/${id}/lida`, { method: "POST", auth: true });
export const marcarTodasNotificacoesLidas = () => request(`/notificacoes/lidas`, { method: "POST", auth: true });

// ---- Painel Admin (papel de admin, via login) ----
export const adminPainel = () => request(`/admin/painel`, { auth: true });
// Publica uma vaga EXTERNA (admin posta pelo recrutador): cola o texto → IA preenche → candidatura vai pro link.
export const adminPublicarVagaExterna = (dados) => request(`/admin/vaga-externa`, { method: "POST", body: dados, auth: true });
// Vaga de PARCEIRA: cria a empresa se preciso e publica com a candidatura acontecendo AQUI — o candidato fica.
export const adminPublicarVagaParceira = (dados) => request(`/admin/vaga-parceira`, { method: "POST", body: dados, auth: true });
export const adminObterConfig = () => request(`/admin/config`, { auth: true });
export const adminSalvarConfig = (dict) => request(`/admin/config`, { method: "PUT", body: dict, auth: true });
export const adminAprovarEmpresa = (id) => request(`/admin/empresas/${id}/aprovar`, { method: "POST", auth: true });
// 🎓 Parceiros de capacitação (17/10/2026): ficha do próprio parceiro, vitrine pública e aprovação no admin.
export const obterMeuParceiro = () => request(`/parceiro/eu`, { auth: true });
export const salvarMeuParceiro = (dados) => request(`/parceiro/eu`, { method: "PUT", body: dados, auth: true });
export const listarParceiros = (skill) => request(`/parceiros${skill ? `?skill=${encodeURIComponent(skill)}` : ""}`);
export const adminListarParceiros = () => request(`/admin/parceiros`, { auth: true });
export const adminAprovarParceiro = (id) => request(`/admin/parceiros/${id}/aprovar`, { method: "POST", auth: true });
// 🎓 Escola parceira com BOLSA (22/10/2026, piloto Times Idiomas): página pública, "já pedi?" e o pedido de contato.
export const listarParceirosBolsa = () => request(`/capacitacao/bolsa`);   // só as ATIVAS, resumo do card
// previa=true: o dono da ficha ou o admin vê a página montada da ficha EM EDIÇÃO (antes da aprovação)
export const obterParceiroBolsa = (slug, previa) => request(`/capacitacao/bolsa/${encodeURIComponent(slug)}${previa ? "?previa=true" : ""}`, { auth: true });
export const meuInteresseBolsa = (slug) => request(`/capacitacao/bolsa/${encodeURIComponent(slug)}/meu`, { auth: true });
export const pedirInteresseBolsa = (slug, dados) => request(`/capacitacao/bolsa/${encodeURIComponent(slug)}/interesse`, { method: "POST", body: dados, auth: true });
// Caminho "/api/..." devolvido pelo backend (ex.: logotipo do parceiro) → URL completa no host da API.
export const urlApi = (caminho) => (caminho ? `${BASE.replace(/\/api\/?$/, "")}${caminho}` : null);

// 🎓 CADASTRO DE PARCEIRO em 8 etapas (22/10/2026): escola com bolsa × professor com aula grátis. Cada etapa salva.
export const metodologiasPadrao = () => request(`/capacitacao/metodologias`);
export const minhasFichasParceiro = () => request(`/parceiro/fichas`, { auth: true });
export const criarFichaParceiro = (tipo) => request(`/parceiro/fichas`, { method: "POST", body: { tipo }, auth: true });
export const obterFichaParceiro = (id) => request(`/parceiro/fichas/${id}`, { auth: true });
export const salvarFichaParceiro = (id, passo, dados) => request(`/parceiro/fichas/${id}`, { method: "PUT", body: { passo, dados }, auth: true });
export const enviarFichaParceiro = (id) => request(`/parceiro/fichas/${id}/enviar`, { method: "POST", auth: true });
export const enviarLogoParceiro = (id, arquivo) => {
  const fd = new FormData();
  fd.append("arquivo", arquivo);
  return uploadMultipart(`/parceiro/fichas/${id}/logo`, fd);
};
export const conferirDocumentoParceiro = (doc) => request(`/parceiro/documento/${encodeURIComponent(doc)}`, { auth: true });
export const interessesDaFicha = (id) => request(`/parceiro/fichas/${id}/interesses`, { auth: true });
export const marcarInteresseAtendido = (id, iid, atendido) =>
  request(`/parceiro/fichas/${id}/interesses/${iid}/atendido`, { method: "POST", body: { atendido }, auth: true });
// admin
// 🤝 22/10/2026: LGPD (retirar autorização), avaliação do aluno, equipe da escola e moderação no admin.
export const retirarInteresseBolsa = (slug) => request(`/capacitacao/bolsa/${encodeURIComponent(slug)}/retirar`, { method: "POST", body: { confirmo: true }, auth: true });
export const avaliarParceiroBolsa = (slug, dados) => request(`/capacitacao/bolsa/${encodeURIComponent(slug)}/avaliacao`, { method: "POST", body: dados, auth: true });
export const meusPedidosCapacitacao = () => request(`/capacitacao/meus-pedidos`, { auth: true });
export const avaliacoesDaFicha = (id) => request(`/parceiro/fichas/${id}/avaliacoes`, { auth: true });
export const responderAvaliacao = (id, aid, texto) => request(`/parceiro/fichas/${id}/avaliacoes/${aid}/responder`, { method: "POST", body: { texto }, auth: true });
export const acessosParceiro = () => request(`/parceiro/acessos`, { auth: true });
export const pedirAcessoParceiro = (id) => request(`/parceiro/acessos/${id}`, { method: "POST", auth: true });
export const equipeDaFicha = (id) => request(`/parceiro/fichas/${id}/equipe`, { auth: true });
export const decidirEquipe = (id, mid, acao) => request(`/parceiro/fichas/${id}/equipe/${mid}/${acao}`, { method: "POST", auth: true });
export const adminAvaliacoesParceiro = (status) => request(`/admin/capacitacao/avaliacoes${status ? `?status=${status}` : ""}`, { auth: true });
export const adminModerarAvaliacao = (id, acao, texto) => request(`/admin/capacitacao/avaliacoes/${id}/${acao}`, { method: "POST", body: texto ? { texto } : {}, auth: true });
export const adminFichasParceiro = () => request(`/admin/capacitacao/fichas`, { auth: true });
export const adminAprovarFicha = (id) => request(`/admin/capacitacao/fichas/${id}/aprovar`, { method: "POST", auth: true });
export const adminDevolverFicha = (id, texto) => request(`/admin/capacitacao/fichas/${id}/devolver`, { method: "POST", body: { texto }, auth: true });
export const adminAtivoFicha = (id, ativo) => request(`/admin/capacitacao/fichas/${id}/ativo`, { method: "POST", body: { ativo }, auth: true });
export const adminMetodologias = () => request(`/admin/capacitacao/metodologias`, { auth: true });
export const adminMetodologia = (nome, acao) => request(`/admin/capacitacao/metodologias`, { method: "POST", body: { nome, acao }, auth: true });
export const adminAulas = () => request(`/admin/aulas`, { auth: true });
// 🔗 link de vagas/ATS enviado pela própria empresa (17/10/2026)
export const obterPortalVagas = () => request(`/empresas/me/portal-vagas`, { auth: true });
export const enviarPortalVagas = (url) => request(`/empresas/me/portal-vagas`, { method: "PUT", body: { url }, auth: true });
export const adminPortaisSugeridos = () => request(`/admin/portais-sugeridos`, { auth: true });
export const adminAceitarPortal = (id) => request(`/admin/portais-sugeridos/${id}/aceitar`, { method: "POST", auth: true });
// 📅 Entrevistas pelo site (17/10/2026)
export const proporEntrevista = (vagaId, usuarioId, dados) => request(`/vagas/${vagaId}/entrevistas/${usuarioId}`, { method: "POST", body: dados, auth: true });
export const entrevistasDaVaga = (vagaId) => request(`/vagas/${vagaId}/entrevistas`, { auth: true });
export const minhasEntrevistas = () => request(`/entrevistas/minhas`, { auth: true });
export const escolherHorarioEntrevista = (id, inicio) => request(`/entrevistas/${id}/escolher`, { method: "POST", body: { inicio }, auth: true });
export const pedirOutrosHorarios = (id, mensagem, sugestoes = []) => request(`/entrevistas/${id}/pedir-outros`, { method: "POST", body: { mensagem, sugestoes }, auth: true });
export const anotacoesEntrevista = (id) => request(`/entrevistas/${id}/anotacoes`, { auth: true });
export const salvarAnotacoesEntrevista = (id, texto) => request(`/entrevistas/${id}/anotacoes`, { method: "PUT", body: { mensagem: texto }, auth: true });
export const aceitarSugestaoEntrevista = (id, inicio) => request(`/entrevistas/${id}/aceitar-sugestao`, { method: "POST", body: { inicio }, auth: true });
export const cancelarEntrevista = (id) => request(`/entrevistas/${id}/cancelar`, { method: "POST", body: {}, auth: true });
// 🧪 Simular uma entrevista (piloto, 23/10/2026): a empresa testa a sala com um colega; não conta em reputação.
export const minhasSimulacoes = () => request(`/simulacoes`, { auth: true });
export const criarSimulacao = (dados) => request(`/simulacoes`, { method: "POST", body: dados, auth: true });
// 3ª etapa (23/10/2026): "Quero testar" rastreável e o formulário "Como foi o teste?" — públicas, sem login
// 🌐 23/10/2026 (Nelson: no piloto vale para todos, sem conta): pedido público + confirmação do e-mail de quem entrevista
export const criarSimulacaoPublica = (dados) => request(`/simulacoes/publica`, { method: "POST", body: dados });
export const obterConfirmacaoSimulacao = (token) => request(`/simulacoes/publica/confirmar/${encodeURIComponent(token)}`);
export const confirmarSimulacao = (token) => request(`/simulacoes/publica/confirmar/${encodeURIComponent(token)}`, { method: "POST", body: {} });
export const cliqueInteresseSimulacao = (codigo) => request(`/simulacoes/interesse/${encodeURIComponent(codigo)}/clique`, { method: "POST", body: {} });
export const visitaInteresseSimulacao = (codigo) => request(`/simulacoes/interesse/${encodeURIComponent(codigo)}/visita`, { method: "POST", body: {} });
export const obterAvaliacaoSimulacao = (token) => request(`/simulacoes/avaliacao/${encodeURIComponent(token)}`);
export const salvarAvaliacaoSimulacao = (token, dados) => request(`/simulacoes/avaliacao/${encodeURIComponent(token)}`, { method: "POST", body: dados });
export const remarcarSimulacao = (id, dados) => request(`/simulacoes/${id}/remarcar`, { method: "POST", body: dados, auth: true });
export const cancelarSimulacao = (id) => request(`/simulacoes/${id}/cancelar`, { method: "POST", body: {}, auth: true });
export const adminSimulacoes = (dias = 30) => request(`/admin/simulacoes?dias=${dias}`, { auth: true });
// 🎓 Aula grátis (etapa 2): horários do parceiro, pedidos, aprovação/sugestão/remarcação.
export const parceiroHorarios = () => request(`/parceiro/horarios`, { auth: true });
export const salvarParceiroHorarios = (lista) => request(`/parceiro/horarios`, { method: "PUT", body: lista, auth: true });
export const parceiroAulas = () => request(`/parceiro/aulas`, { auth: true });
export const parceiroLivres = () => request(`/parceiro/livres`, { auth: true });
export const parceiroAcaoAula = (id, acao, inicio) => request(`/parceiro/aulas/${id}/${acao}`, { method: "POST", body: inicio ? { inicio } : {}, auth: true });
export const livresDoParceiro = (id) => request(`/parceiros/${id}/livres`);
export const pedirAula = (dados) => request(`/aulas`, { method: "POST", body: dados, auth: true });
export const minhasAulas = () => request(`/aulas/minhas`, { auth: true });
export const acaoMinhaAula = (id, acao, inicio) => request(`/aulas/${id}/${acao}`, { method: "POST", body: inicio ? { inicio } : {}, auth: true });
export const adminReenviarConfirmacao = (id) => request(`/admin/usuarios/${id}/reenviar-confirmacao`, { method: "POST", auth: true });
// Espelho de vagas: PRÉVIA só lê o portal da empresa (não grava nada); sincronizar exige o toggle ligado
// E o destino da candidatura declarado pela empresa ("eairh" ou "portal").
export const adminEspelhoPrevia = (empresaId, url, detalhe = false, maxDetalhe = 5) =>
  request(`/admin/espelho/previa?empresaId=${empresaId}${url ? `&url=${encodeURIComponent(url)}` : ""}&detalhe=${detalhe}&maxDetalhe=${maxDetalhe}`, { auth: true });
export const adminEspelhoSincronizar = (empresaId, url, destino, opcoes = {}) =>
  request(`/admin/espelho/sincronizar?empresaId=${empresaId}${url ? `&url=${encodeURIComponent(url)}` : ""}&destino=${destino}` +
    `&desativarSumidas=${!!opcoes.desativarSumidas}`, { method: "POST", auth: true });
export const adminSalvarPortalVagas = (empresaId, url) =>
  request(`/admin/empresas/${empresaId}/portal-vagas`, { method: "POST", body: { url }, auth: true });
export const adminObterPortalVagas = (empresaId) =>
  request(`/admin/empresas/${empresaId}/portal-vagas`, { auth: true }); // fonte já salva (preenche o campo ao trocar de empresa)
export const adminExcluirVaga = (id) =>
  request(`/admin/vaga/${id}`, { method: "DELETE", auth: true }); // desfaz espelho publicado por engano (leva as candidaturas junto)
// Tira do ar (2 = Pausada) ou devolve ao ar (0 = Aberta) SEM excluir: candidaturas e histórico ficam de pé.
export const adminAlterarStatusVaga = (id, status) =>
  request(`/admin/vaga/${id}/status?status=${status}`, { method: "POST", auth: true });
export const adminListarModelos = () => request(`/admin/modelos`, { auth: true });
export const adminCriarModelo = (dados) => request(`/admin/modelos`, { method: "POST", body: dados, auth: true });
export const adminAtualizarModelo = (id, dados) => request(`/admin/modelos/${id}`, { method: "PUT", body: dados, auth: true });
export const adminRemoverModelo = (id) => request(`/admin/modelos/${id}`, { method: "DELETE", auth: true });
export const adminModeloPadrao = (id) => request(`/admin/modelos/${id}/padrao`, { method: "POST", auth: true });
export const adminPreviewModelo = (assunto, corpo) => request(`/admin/modelos/preview`, { method: "POST", body: { caso: "EmpresaCitada", nome: "preview", assunto, corpo }, auth: true });
// Acolhimento — a mensagem do eaiRH a quem teve o processo encerrado (nossa voz, não a da empresa)
export const adminListarAcolhimento = () => request(`/admin/acolhimento/modelos`, { auth: true });
export const adminCriarAcolhimento = (dados) => request(`/admin/acolhimento/modelos`, { method: "POST", body: dados, auth: true });
export const adminAtualizarAcolhimento = (id, dados) => request(`/admin/acolhimento/modelos/${id}`, { method: "PUT", body: dados, auth: true });
export const adminRemoverAcolhimento = (id) => request(`/admin/acolhimento/modelos/${id}`, { method: "DELETE", auth: true });
export const adminAcolhimentoPadrao = (id) => request(`/admin/acolhimento/modelos/${id}/padrao`, { method: "POST", auth: true });
export const adminAcolhimentoPrevia = () => request(`/admin/acolhimento/previa`, { auth: true });
export const adminAcolhimentoEnviar = () => request(`/admin/acolhimento/enviar`, { method: "POST", auth: true });
export const adminClassificarEtapas = () => request(`/admin/acolhimento/etapas/classificar`, { method: "POST", auth: true });
export const adminListarEtapasAcolhimento = () => request(`/admin/acolhimento/etapas`, { auth: true });
export const adminNaturezaEtapa = (id, natureza) => request(`/admin/acolhimento/etapas/${id}/natureza`, { method: "PUT", body: { natureza }, auth: true });

export const adminListarModelosWpp = () => request(`/admin/modelos-whatsapp`, { auth: true });
export const adminCriarModeloWpp = (dados) => request(`/admin/modelos-whatsapp`, { method: "POST", body: dados, auth: true });
export const adminAtualizarModeloWpp = (id, dados) => request(`/admin/modelos-whatsapp/${id}`, { method: "PUT", body: dados, auth: true });
export const adminRemoverModeloWpp = (id) => request(`/admin/modelos-whatsapp/${id}`, { method: "DELETE", auth: true });
export const adminModeloWppPadrao = (id) => request(`/admin/modelos-whatsapp/${id}/padrao`, { method: "POST", auth: true });
export const adminPreviewModeloWpp = (corpo) => request(`/admin/modelos-whatsapp/preview`, { method: "POST", body: { caso: "x", publico: "candidato", nome: "preview", corpo }, auth: true });
export const adminPreviewModeloWppNeutro = (corpoSemConsentimento) => request(`/admin/modelos-whatsapp/preview`, { method: "POST", body: { caso: "x", publico: "candidato", nome: "preview", corpo: corpoSemConsentimento }, auth: true });
export const adminListarSegredos = () => request(`/admin/segredos`, { auth: true });
export const adminSalvarSegredo = (chave, valor) => request(`/admin/segredos`, { method: "PUT", body: { chave, valor }, auth: true });
export const adminRevelarSegredo = (chave) => request(`/admin/segredos/revelar?chave=${encodeURIComponent(chave)}`, { auth: true });
// Instagram (@eairh.oficial): o token fica no cofre e NUNCA volta para o navegador — estas rotas só dizem
// se a conexão está de pé e mandam publicar. Quem conversa com a Meta é o servidor.
export const adminInstagram = () => request(`/admin/instagram`, { auth: true });
export const adminInstagramPublicar = (imagemUrl, legenda) =>
  request(`/admin/instagram/publicar`, { method: "POST", body: { imagemUrl, legenda }, auth: true });
// Biblioteca de imagens dos cards (wwwroot/biblioteca). As fotos são servidas como arquivo estático;
// estas rotas só listam, buscam no Pexels e adicionam/removem.
export const adminBiblioteca = () => request(`/admin/biblioteca`, { auth: true });
export const adminBibliotecaBuscar = (termo, idioma) =>
  request(`/admin/biblioteca/buscar?termo=${encodeURIComponent(termo)}&idioma=${idioma || "pt"}`, { auth: true });
export const adminBibliotecaAdicionar = (dados) => request(`/admin/biblioteca/adicionar`, { method: "POST", body: dados, auth: true });
export const adminBibliotecaRemover = (tema, arquivo) =>
  request(`/admin/biblioteca/${encodeURIComponent(tema)}/${encodeURIComponent(arquivo)}`, { method: "DELETE", auth: true });
export const adminTestarEmail = () => request(`/admin/testar-email`, { method: "POST", auth: true });
export const adminTestarChave = (qual) => request(`/admin/testar-chave`, { method: "POST", body: { qual }, auth: true });
export const adminTestarWhatsappDisparo = () => request(`/admin/testar-whatsapp`, { method: "POST", auth: true });
export const adminEmpresasContas = () => request(`/admin/empresas-contas`, { auth: true });
export const adminAtuacao = (todos = false) => request(`/admin/atuacao${todos ? "?todos=true" : ""}`, { auth: true });
export const adminAtuacaoAcao = (id, acao, canal, nota) => request(`/admin/atuacao/${id}`, { method: "POST", body: { acao, canal, nota }, auth: true });
export const adminLista = (tipo, q = "") => request(`/admin/lista/${tipo}${q ? `?q=${encodeURIComponent(q)}` : ""}`, { auth: true });
export const adminUsuarioDetalhe = (id) => request(`/admin/usuarios/${id}/detalhe`, { auth: true });
export const adminListarModeracao = () => request(`/admin/moderacao`, { auth: true });
export const adminRestaurarDenuncia = (id) => request(`/admin/denuncias/${id}/restaurar`, { method: "POST", auth: true });
export const adminOcultarDenuncia = (id) => request(`/admin/denuncias/${id}/ocultar`, { method: "POST", auth: true });
export const adminResolverContestacao = (id) => request(`/admin/denuncias/${id}/resolver-contestacao`, { method: "POST", auth: true });
// Link mágico: gera/obtém o link de resposta (p/ copiar e mandar à empresa) + aprova/rejeita a resposta que a empresa enviou.
export const adminLinkResposta = (id) => request(`/admin/denuncias/${id}/link-resposta`, { method: "POST", auth: true });
export const adminLinkRespostaEmpresa = (empresaId) => request(`/admin/empresas/${empresaId}/link-resposta`, { method: "POST", auth: true });
export const adminAprovarRespostaPendente = (id) => request(`/admin/denuncias/${id}/resposta-pendente/aprovar`, { method: "POST", auth: true });
export const adminRejeitarRespostaPendente = (id) => request(`/admin/denuncias/${id}/resposta-pendente/rejeitar`, { method: "POST", auth: true });
export const adminAprovarTreplicaPendente = (id) => request(`/admin/denuncias/${id}/treplica-pendente/aprovar`, { method: "POST", auth: true });
export const adminRejeitarTreplicaPendente = (id) => request(`/admin/denuncias/${id}/treplica-pendente/rejeitar`, { method: "POST", auth: true });
// Avisa o CANDIDATO que estamos tentando contato com a empresa (dispara o e-mail "estamos tentando contato").
export const adminContatarEmpresa = (id) => request(`/admin/denuncias/${id}/contato-empresa`, { method: "POST", auth: true });
// Marca TODOS os relatos abertos de uma empresa como contatada (outreach manual, SEM e-mail) → tira da fila "Abordar".
export const adminMarcarContatada = (empresaId) => request(`/admin/empresas/${empresaId}/marcar-contatada`, { method: "POST", auth: true });
// Contata a empresa de dentro do painel. canal="email" envia na hora e marca contatada;
// canal="whatsapp" SO enfileira na ponte — quem marca contatada e a ponte, ao confirmar o envio.
export const adminContatarEmpresaPor = (empresaId, canal) =>
  request(`/admin/empresas/${empresaId}/contatar?canal=${canal}`, { method: "POST", auth: true });
// Publica uma atualização da eaiRH na timeline do relato (notifica o autor). imagem = File opcional (print). multipart.
export const adminAtualizarRelato = (id, { texto, publica, imagem }) => {
  const fd = new FormData();
  fd.append("texto", texto);
  fd.append("publica", publica ? "true" : "false");
  if (imagem) fd.append("imagem", imagem);
  return uploadMultipart(`/admin/denuncias/${id}/atualizacao`, fd);
};
// URL pública do print de uma atualização (servido por GUID).
export const urlImagemAtualizacao = (denunciaId, aid) => `${BASE}/denuncias/${denunciaId}/atualizacoes/${aid}/imagem`;
// Admin: alterar visibilidade de uma atualização (público <-> privado / "ocultar").
export const adminVisibilidadeAtualizacao = (denunciaId, aid, publica) => request(`/admin/denuncias/${denunciaId}/atualizacao/${aid}/visibilidade`, { method: "POST", auth: true, body: { publica } });
// Admin: apagar uma atualização da timeline.
export const adminApagarAtualizacao = (denunciaId, aid) => request(`/admin/denuncias/${denunciaId}/atualizacao/${aid}`, { method: "DELETE", auth: true });
// Admin envia e-mail livre ao RESPONSÁVEL do processo (contato do relato), em nome da eaiRH. copiarCandidato = CC no autor.
export const adminEmailResponsavel = (id, { assunto, corpo, copiarCandidato }) =>
  request(`/admin/denuncias/${id}/email-responsavel`, { method: "POST", body: { assunto, corpo, copiarCandidato: !!copiarCandidato }, auth: true });
// Admin envia WhatsApp livre ao RESPONSÁVEL do processo (WhatsApp do contato do relato), em nome da eaiRH.
export const adminWhatsappResponsavel = (id, { texto, ignorarConsentimento }) =>
  request(`/admin/denuncias/${id}/whatsapp-responsavel`, { method: "POST", body: { texto, ignorarConsentimento }, auth: true });
// Acompanhamento de prazos (SLA): relatos reais em aberto com status do prazo + se contatamos a empresa.
export const adminPrazos = () => request(`/admin/prazos`, { auth: true });
// Catálogo de ATS + qual cada empresa usa. O catálogo diz o que CADA integração entrega (e o que não entrega).
export const adminIntegracoes = () => request(`/admin/integracoes`, { auth: true });
export const adminMovimento = () => request(`/admin/movimento`, { auth: true });
// 🔗 Integração VagasLume (20/10/2026)
export const vagasLumePreparar = (retorno) => request(`/integracoes/vagaslume/preparar?retorno=${encodeURIComponent(retorno)}`, { auth: true });
export const vagasLumeAutorizar = (retorno, estado, decisao) => request(`/integracoes/vagaslume/autorizar`, { method: "POST", body: { retorno, estado, decisao }, auth: true });
export const vagasLumeStatus = () => request(`/integracoes/vagaslume/status`, { auth: true });
export const vagasLumeDesconectar = () => request(`/integracoes/vagaslume/desconectar-eairh`, { method: "POST", auth: true });
export const adminMovimentoWhatsapp = (periodo = "hoje") => request(`/admin/movimento-whatsapp?periodo=${periodo}`, { auth: true });
export const adminSalvarAts = (empresaId, ats) =>
  request(`/admin/integracoes/${empresaId}`, { method: "PUT", body: { ats }, auth: true });
// Acompanhamento do funil das empresas com ATS integrado. empresaId/vagaId afunilam a leitura.
export const adminAcompanhamento = (empresaId, vagaId) => {
  const q = new URLSearchParams();
  if (empresaId) q.set("empresaId", empresaId);
  if (vagaId) q.set("vagaId", vagaId);
  const s = q.toString();
  return request(`/admin/acompanhamento${s ? `?${s}` : ""}`, { auth: true });
};
// Feedbacks dos candidatos (link /feedback/{token}): resumo + respostas completas. Ver a sub-aba "💬 Feedbacks".
export const adminFeedbacks = () => request(`/admin/feedbacks`, { auth: true });
// Indicadores do painel: série diária, curva por hora, origens, empresas e funil.
// `dias` recorta a janela (padrão 30). Ver a aba "📈 Indicadores".
export const adminIndicadores = (dias) =>
  request(`/admin/indicadores${dias ? `?dias=${dias}` : ""}`, { auth: true });

// ---- Prospecção comercial (aba do admin) ----
// LISTA PRÓPRIA de empresas-alvo: prospecção existe para trazer quem ainda NÃO é do portal.
// A lista também é o registro de quem já foi abordado (status + último contato).
export const adminProspects = () => request(`/admin/prospeccao`, { auth: true });
export const adminProspectFicha = (id) => request(`/admin/prospeccao/${id}`, { auth: true });
export const adminProspectSalvar = (p) => request(`/admin/prospeccao`, { method: "POST", body: p, auth: true });
export const adminProspectRemover = (id) => request(`/admin/prospeccao/${id}`, { method: "DELETE", auth: true });
export const adminProspeccaoModelos = () => request(`/admin/prospeccao/modelos`, { auth: true });
export const adminProspeccaoSalvarContato = (c) => request(`/admin/prospeccao/contato`, { method: "POST", body: c, auth: true });
export const adminProspeccaoRemoverContato = (id) => request(`/admin/prospeccao/contato/${id}`, { method: "DELETE", auth: true });
export const adminProspeccaoPrevia = (modeloId, contatoId) =>
  request(`/admin/prospeccao/previa?modeloId=${modeloId}&contatoId=${contatoId}`, { auth: true });
export const adminProspeccaoEnviar = (dados) => request(`/admin/prospeccao/enviar`, { method: "POST", body: dados, auth: true });
// Configuração da caixa comercial + teste. A senha é gravada no cofre cifrado; nunca volta na leitura.
export const adminComercialConfig = () => request(`/admin/prospeccao/config`, { auth: true });
export const adminComercialSalvarConfig = (c) => request(`/admin/prospeccao/config`, { method: "POST", body: c, auth: true });
export const adminComercialTestar = (para) => request(`/admin/prospeccao/testar`, { method: "POST", body: { para: para || null }, auth: true });
// Quem pode abrir a aba Prospecção. Só o dono lê/edita — o servidor recusa os demais.
export const adminAcessoComercial = () => request(`/admin/prospeccao/acesso`, { auth: true });
export const adminSalvarAcessoComercial = (emails) => request(`/admin/prospeccao/acesso`, { method: "POST", body: { emails }, auth: true });

// Fecha o ciclo: empresa não respondeu no prazo → marca o relato como "Não resolvida".
export const adminEncerrarSemResposta = (id) => request(`/admin/denuncias/${id}/encerrar-sem-resposta`, { method: "POST", auth: true });

// ---- Enums (espelham o backend) ----
export const ETAPAS = [
  { v: 0, t: "Inscrição / triagem" },
  { v: 1, t: "Teste / desafio técnico" },
  { v: 2, t: "Entrevista com RH" },
  { v: 3, t: "Entrevista com gestor" },
  { v: 4, t: "Etapa final / proposta" },
];
export const TIPOS = [
  { v: 0, t: "Sem resposta" },
  { v: 1, t: "Má conduta" },
  { v: 2, t: "Processo longo / sem comunicação" },
  { v: 3, t: "Outro" },
];
export const DESFECHOS = [
  { v: 0, t: "Ainda em andamento" },
  { v: 1, t: "Empresa sumiu (sem retorno)" },
  { v: 2, t: "Fui rejeitado" },
  { v: 3, t: "Desisti" },
  { v: 4, t: "Recebi proposta" },
];
export const TIPOS_RETORNO = [
  { v: 0, t: "Prefiro não informar" },
  { v: 1, t: "👻 Não recebi retorno (sumiço)" },
  { v: 2, t: "🤖 Resposta automática / genérica" },
  { v: 3, t: "💬 Retorno personalizado" },
];
export const MOTIVOS_REPORTE = ["Informação falsa", "Ofensa / linguagem imprópria", "Dados pessoais expostos", "Spam / propaganda", "Outro"];
